import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useLocale } from '@/hooks/useLocale';
import { submitEnterpriseEnquiry } from '../publicApi';
import { Section, SectionHeading } from './Section';
import styles from './EnterpriseForm.module.css';
import { cn } from '@/lib/utils';

type FieldErrors = {
  name?: string;
  email?: string;
  companyName?: string;
  seats?: string;
  message?: string;
};

export function EnterpriseForm() {
  const { t } = useTranslation('marketing');
  const { locale } = useLocale();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [seats, setSeats] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [formStartedAt] = useState(() => new Date().toISOString());
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [received, setReceived] = useState(false);

  function fieldError(path: string, code: string): string {
    if (path === 'email' && code === 'invalid_string') return t('enterprise.invalidEmail');
    if (path === 'seats') return t('enterprise.invalidSeats');
    return t('enterprise.required');
  }

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    setSubmitting(true);
    try {
      await validateAndSend();
    } catch {
      // Schema chunk failed to load (stale deploy, flaky network) or the request failed.
      setSubmitError(t('enterprise.error'));
    } finally {
      setSubmitting(false);
    }
  }

  async function validateAndSend() {
    const seatsValue = seats.trim() === '' ? undefined : Number(seats);
    // Loaded on submit so zod stays out of the public landing bundle (CX-396).
    const { enterpriseEnquirySchema } = await import('@connexio/shared');
    const parsed = enterpriseEnquirySchema.safeParse({
      name: name.trim(),
      email: email.trim(),
      companyName: companyName.trim(),
      seats: Number.isNaN(seatsValue as number) ? seats : seatsValue,
      message: message.trim() || undefined,
      locale,
      _hp: honeypot,
      _formStartedAt: formStartedAt,
    });

    if (!parsed.success) {
      const next: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const path = String(issue.path[0] ?? '');
        if (path === 'name' || path === 'email' || path === 'companyName' || path === 'seats' || path === 'message') {
          next[path] = fieldError(path, issue.code);
        }
      }
      if (!email.trim() && next.email) next.email = t('enterprise.required');
      setErrors(next);
      return;
    }

    setErrors({});
    await submitEnterpriseEnquiry(parsed.data);
    setReceived(true);
  }

  return (
    <Section id="enterprise" tone="light" labelledBy="enterprise-title">
      <div className={styles.grid}>
        <div>
          <SectionHeading
            id="enterprise-title"
            title={t('enterprise.title')}
            description={t('enterprise.lead')}
          />
        </div>

        <div className={styles.success}>
          {received ? (
            <p role="status" aria-live="polite" className={cn(styles.success2, 'text-body-l')}>
              {t('enterprise.success')}
            </p>
          ) : (
            <form className={styles.form} onSubmit={(event) => void onSubmit(event)} noValidate>
              <input
                type="text"
                name="_hp"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ display: 'none', position: 'absolute', left: '-9999px' }}
                tabIndex={-1}
                aria-hidden="true"
                autoComplete="off"
              />
              <Input
                id="enterprise-name"
                label={t('enterprise.name')}
                name="name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={errors.name}
              />
              <Input
                id="enterprise-email"
                label={t('enterprise.email')}
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={errors.email}
              />
              <Input
                id="enterprise-company"
                label={t('enterprise.company')}
                name="companyName"
                autoComplete="organization"
                required
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                error={errors.companyName}
              />
              <Input
                id="enterprise-seats"
                label={t('enterprise.seats')}
                name="seats"
                type="number"
                inputMode="numeric"
                min={1}
                max={10000}
                value={seats}
                onChange={(e) => setSeats(e.target.value)}
                error={errors.seats}
              />
              <Textarea
                id="enterprise-message"
                label={t('enterprise.message')}
                name="message"
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                error={errors.message}
              />
              {submitError ? (
                <p role="alert" className={cn(styles.text, 'text-body-m')}>
                  {submitError}
                </p>
              ) : null}
              <Button type="submit" size="lg" disabled={submitting}>
                {t('enterprise.submit')}
              </Button>
            </form>
          )}
        </div>
      </div>
    </Section>
  );
}
